import React, { useEffect, useRef } from 'react';
import styles from './MedicinesSearchResults.module.scss';
import {
  Configure,
  InstantSearch,
  useInfiniteHits,
  UseInfiniteHitsProps,
  useInstantSearch,
  useStats,
} from 'react-instantsearch';
import { MEDICINES, grad1, grad2, /*grad1, grad2,*/ leumitSearchClient } from 'assets/constants';
import strings from "assets/strings";
import Text from "components/text/Text";
import { MedicinesResult } from './MedicinesResult';
import type { BaseHit, Hit } from 'instantsearch.js';
import { MedicinesResultType } from 'types';
//import { Button } from 'components/buttons/button/Button';
import CustomSearchBox from 'components/inputs/customSearchbox/CustomSearchBox';
import getRouting from './routing';
import SvgGenerator from 'components/svgGenerator/SvgGenerator';
const routing = getRouting(MEDICINES);

function NoResultsBoundary({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) {
  const { results, status } = useInstantSearch();

  const extendedResults = results as typeof results & {
    __isError?: boolean;
    __errorStatus?: number;
  };

  if (extendedResults.__isError && extendedResults.__errorStatus === 403) {
    return (
      <div className="noMessagesWrapper">
        {/* <SvgGenerator
          name="Magnifier"
          className="magnifierIcon"
          objectName="medicalCenterSearchResults"
        /> */}
        <Text text={strings.globalStrings.networkError} />
      </div>
    );
  }

  // Don't show the no results message while loading
  if (status === 'loading') {
    return <>{children}</>;
  }

  // Only show no results message when the search is complete and there are no hits
  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return <>{children}</>;
}

function NoResults() {
  const noResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (noResultsRef.current) {
      noResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className={styles.noMessagesWrapper}
      role="status"
      aria-live="assertive"
      tabIndex={-1}
      ref={noResultsRef}>
      <SvgGenerator name="Magnifier" className="magnifierIcon" objectName="doctorSearchResults" />
      <Text text={strings.globalStrings.noResultsFound} />
    </div>
  );
}


function InfiniteHitsComponent(props: UseInfiniteHitsProps) {
  const { hits, showMore, isLastPage } = useInfiniteHits(props);
  const [nextFocusIndex, setCurrentFocusIndex] = React.useState(0);
  const medicineResultRef = useRef<HTMLDivElement>(null);

  const handleShowMore = () => {
    showMore();
    setCurrentFocusIndex(nextFocusIndex + 20);
    medicineResultRef.current?.focus();
  }

  return (
    hits.length > 0 ? <div className='animate'>
      {hits.map((hit: Hit<BaseHit>, index) => {
        return <MedicinesResult hit={hit as MedicinesResultType} key={`${hit.objectID}_${index}`} />;
      })
      }
      {!isLastPage && <button className='ais-InfiniteHits-loadMore' onClick={handleShowMore} disabled={isLastPage}>{strings.globalStrings.moreResults}</button>}
    </div> : null
  )
}

export function MedicinesSearchResults() {
  const Stats = () => {
    const { nbHits } = useStats();
    return (
      nbHits ? <div className={styles.resultsCount}>
        {nbHits === 1 ?
          <Text
            className={styles.title}
            text={strings.globalStrings.foundOneResult} /> :
          <Text
            className={styles.title}
            text={`${strings.medicalCentersSearchResults.found} ${nbHits} ${strings.medicalCentersSearchResults.results}`}
          />
        }
        <Text text={strings.medicinesSearchResults.termsCondition1} className={styles.termCondition} />
        <Text text={strings.medicinesSearchResults.termsCondition2} className={styles.termCondition} />
      </div> : null
    );
  };

  return (
    <div className="row ais-InstantSearch">
      <InstantSearch
        indexName={MEDICINES}
        searchClient={leumitSearchClient}
        routing={routing}
      >
        <Configure snippetEllipsisText="…" hitsPerPage={1000} />
        <div className="col-12 ais-SearchResults">
          <Configure />
          <div className={styles.searchDiv}>
            <Text text={strings.medicinesSearchResults.medicinesAndEquipmentSearch} className={styles.serachTitle} />
            <Text text={strings.medicinesSearchResults.medicinesAndEquipmentSearchRequiredSearch} className={styles.subSerachTitle} />
            <div className={styles.searchBox}>
              <CustomSearchBox color1={grad1} color2={grad2} btnText={strings.globalStrings.search} isAutoComplete indexName={MEDICINES} />
            </div>
          </div>
          <Stats />
          <NoResultsBoundary fallback={<NoResults />}>
            <InfiniteHitsComponent />
          </NoResultsBoundary>
        </div>
      </InstantSearch>
    </div>
  );
}


